import {
  CheckCircle as ApprovedIcon,
  Close as RejectedIcon,
  Description as RequirementsIcon,
  Info as InfoIcon,
  PersonAdd as ManualAddIcon,
  PlayArrow as RunIcon,
  Schedule as TimeIcon,
  Star as FinalIcon,
  StarBorder as ShortlistIcon,
  Undo as MoveBackIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import LogoLoader from "../../common/LogoLoader";
import { ActiveWork } from "../../../services/researchApi";
import {
  AgentActivity,
  ResearchEntity,
  WorkflowStage,
} from "../../../types/researchWorkflow";
import ActiveRunsModal from "../ActiveRunsModal";
import DeepResearchReportView from "./DeepResearchReportView";

interface AgentTabProps {
  currentStage: WorkflowStage;
  activeWork: ActiveWork;
  activities: AgentActivity[];
  activitiesLoading: boolean;
  selectedEntity?: ResearchEntity;
  onClearSelection: () => void;
}

const AgentTab: React.FC<AgentTabProps> = ({
  currentStage,
  activeWork,
  activities,
  activitiesLoading,
  selectedEntity,
  onClearSelection,
}) => {
  // Force re-render every 30 seconds to update relative timestamps
  const [, setNow] = useState(Date.now());
  const [activeRunsOpen, setActiveRunsOpen] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);
  const getStageLabel = (stage: WorkflowStage) => {
    switch (stage) {
      case "requirements":
        return "Requirements Definition";
      case "longList":
        return "Discovery";
      case "shortList":
        return "Evaluation";
      case "finalSelection":
        return "Final Selection";
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const getActivityIcon = (action: string) => {
    // Requirements activities
    if (action.includes("Requirements")) {
      return <RequirementsIcon sx={{ fontSize: 14, color: "info.main" }} />;
    }

    // Decision activities
    if (action.includes("Moved to Evaluation")) {
      return <ApprovedIcon sx={{ fontSize: 14, color: "success.main" }} />;
    }
    if (action.includes("Moved to Discovery")) {
      return <MoveBackIcon sx={{ fontSize: 14, color: "text.secondary" }} />;
    }
    if (action.includes("Dismissed")) {
      return <RejectedIcon sx={{ fontSize: 14, color: "error.main" }} />;
    }

    // Shortlist / removal from final
    if (action.includes("Removed from Final")) {
      return <ShortlistIcon sx={{ fontSize: 14, color: "warning.main" }} />;
    }
    if (action === "Added to Final Selection") {
      return <FinalIcon sx={{ fontSize: 14, color: "warning.main" }} />;
    }
    if (action.includes("Shortlist")) {
      return <ShortlistIcon sx={{ fontSize: 14, color: "warning.main" }} />;
    }

    // Discovery/Evaluation/Run activities
    if (
      action.includes("Discovery") ||
      action.includes("Evaluation") ||
      action.includes("Run")
    ) {
      return <RunIcon sx={{ fontSize: 14, color: "primary.main" }} />;
    }

    // Manual add activities
    if (action.includes("Manually Added")) {
      return <ManualAddIcon sx={{ fontSize: 14, color: "info.main" }} />;
    }

    // Default icon
    return <InfoIcon sx={{ fontSize: 14, color: "text.secondary" }} />;
  };

  const getUserDisplayName = (
    userEmail?: string,
    userUsername?: string,
  ): string => {
    // If no user info, it's an agent action
    if (!userEmail && !userUsername) {
      return "Assistant";
    }

    // Use email prefix (before @) if available
    if (userEmail) {
      const emailPrefix = userEmail.split("@")[0];
      // Shorten if too long (max 12 chars)
      return emailPrefix.length > 12
        ? emailPrefix.substring(0, 12) + "..."
        : emailPrefix;
    }

    // Fallback to username
    if (userUsername) {
      return userUsername.length > 12
        ? userUsername.substring(0, 12) + "..."
        : userUsername;
    }

    return "Unknown";
  };

  // Show all recent activities (not filtered by stage)
  const relevantActivities = activities.slice(0, 20);

  // If entity is selected and has a deep research report, show the report
  if (selectedEntity?.deepResearchReport) {
    return (
      <DeepResearchReportView
        report={selectedEntity.deepResearchReport}
        entityName={selectedEntity.name}
        onClose={onClearSelection}
      />
    );
  }

  // Otherwise show the default activity log view
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* In-progress indicator — aggregate count across candidates (evaluation)
          and discovery runs. Per-candidate detail lives in the candidates list,
          so this stays a non-expandable summary (#888). */}
      <Box sx={{ p: 2.5, borderBottom: 1, borderColor: "divider" }}>
        {(() => {
          const evalActive =
            activeWork.evaluation.queued + activeWork.evaluation.running;
          const discoveryActive = activeWork.discovery.length;
          const parts: string[] = [];
          if (evalActive > 0) {
            const queuedNote =
              activeWork.evaluation.queued > 0
                ? ` · ${activeWork.evaluation.queued} queued`
                : "";
            parts.push(`Evaluating ${evalActive}${queuedNote}`);
          }
          if (discoveryActive > 0) {
            parts.push(
              `${discoveryActive} discovery${
                discoveryActive === 1 ? "" : " runs"
              } running`,
            );
          }
          const busy = parts.length > 0;
          return (
            <>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="500"
                  sx={{ lineHeight: "24px" }}
                >
                  In Progress
                </Typography>
                <Tooltip title="View all active runs across your projects" arrow>
                  <Chip
                    label={busy ? parts.join(" · ") : "Idle"}
                    size="small"
                    color={busy ? "success" : "default"}
                    onClick={() => setActiveRunsOpen(true)}
                    sx={{
                      height: 24,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  />
                </Tooltip>
              </Box>
              {busy && <LinearProgress sx={{ mt: 1.5 }} />}
            </>
          );
        })()}
      </Box>

      {/* Activity Log */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          gutterBottom
          sx={{ display: "block", mb: 1 }}
        >
          Recent Activity
        </Typography>

        {activitiesLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "text.secondary",
            }}
          >
            <LogoLoader size="section" surface="paper" label="Loading activities" sx={{ mb: 1 }} />
            <Typography variant="body2">Loading activities...</Typography>
          </Box>
        ) : relevantActivities.length === 0 ? (
          <Box
            sx={{
              py: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <InfoIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">No activity yet</Typography>
          </Box>
        ) : (
          <List dense sx={{ p: 0 }}>
            {relevantActivities.map((activity) => (
              <ListItem
                key={activity.id}
                sx={{
                  px: 0,
                  py: 1,
                  borderBottom: 1,
                  borderColor: "divider",
                  "&:last-child": {
                    borderBottom: 0,
                  },
                }}
              >
                <Box sx={{ width: "100%" }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 0.5,
                    }}
                  >
                    {getActivityIcon(activity.action)}
                    <Typography
                      variant="body2"
                      fontWeight="600"
                      sx={{ flex: 1 }}
                    >
                      {activity.action}
                    </Typography>
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 0.5 }}
                  >
                    {activity.details}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <TimeIcon sx={{ fontSize: 12, color: "text.disabled" }} />
                      <Typography variant="caption" color="text.disabled">
                        {formatTimestamp(activity.timestamp)}
                      </Typography>
                    </Box>
                    <Chip
                      label={getUserDisplayName(
                        activity.userEmail,
                        activity.userUsername,
                      )}
                      size="small"
                      sx={{
                        height: 16,
                        fontSize: "0.65rem",
                        backgroundColor:
                          !activity.userEmail && !activity.userUsername
                            ? "primary.dark"
                            : "rgba(255, 255, 255, 0.08)",
                        borderColor: "rgba(255, 255, 255, 0.12)",
                        "& .MuiChip-label": {
                          px: 1,
                        },
                      }}
                      variant="outlined"
                      title={
                        activity.userEmail ||
                        activity.userUsername ||
                        "AI Assistant"
                      }
                    />
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}
      </Box>

      {/* Summary Stats */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: "divider",
          backgroundColor: "#1e1e1e",
        }}
      >
        <Typography variant="caption" color="text.secondary" gutterBottom>
          Stage Summary
        </Typography>
        <Typography variant="body2">
          {relevantActivities.length} activities logged
        </Typography>
      </Box>

      <ActiveRunsModal
        open={activeRunsOpen}
        onClose={() => setActiveRunsOpen(false)}
      />
    </Box>
  );
};

export default AgentTab;
